import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType } from './_helper.js';

defineMailTemplate(MailTemplateType.MEETING_INFOS, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Termininformationen',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>die Veranstaltung findet in wenigen Tagen statt. Der Termin findet digital in der Form eines Videocall unter folgendem Link statt:</p>
<p><a>{{${MailTemplateVars.MEETING_LINK}}}</a></p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>The event will take place in a few days. The appointment will be held digitally in the form of a video call at the following link:</p>
<p><a>{{${MailTemplateVars.MEETING_LINK}}}</a></p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.MEETING_INFOS),
  attachments: [],
  displayName: 'Termin Informationen',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.MEETING_INFOS),
});

export async function notifyMeetingInfos(data: { participationID: string; appointmentID: string }) {
  const participationCol = await getRawCollection<Participation>(Participation._collectionName);
  const participation = await participationCol.findOne<Participation>({ _id: data.participationID });
  if (!participation) throw new Error('participation not found: ' + data.participationID);

  const userCol = await getRawCollection<User>(User._collectionName);
  const user = await userCol.findOne<User>({ _id: participation.userID });
  if (!user) throw new Error('user not found: ' + participation.userID);

  const workshop = await getByID(
    Workshop,
    participation.productID,
  )({
    _id: 1,
    institutionID: 1,
    name: 1,
    lastMeetingInfoMailSent: 1,
    settings: {
      _id: 1,
      _class: 1,
      meetingInfoMailLeadTime: 1,
      inheritedMeetingInfoMailLeadTime: 1,
      evaluationLink: 1,
      inheritedEvaluationLink: 1,
      evaluationLinkCode: 1,
      inheritedEvaluationLinkCode: 1,
    },
    appointments: { _id: 1, start: 1, end: 1, location: 1, lastReminderMailSent: 1, meetingLink: { link: 1 }, name: 1 },
  });

  if (!workshop) {
    logger.info(
      { _id: participation.productID },
      'No workshop found for given id. Therefore no meetingInfoMails were sent.',
    );
    throw new Error('No workshop found for given id');
  }

  const appointment = workshop.appointments?.find(a => a._id === data.appointmentID);
  if (!appointment) throw new Error('appointment not found: ' + data.appointmentID);

  // We use getByID instead of getRawCollection to retrieve color definitions
  const institution = await getByID(
    Institution,
    workshop.institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) {
    logger.info({ institutionID: workshop.institutionID }, "Couldn't find institution. No meeting info mail was sent.");
    throw new Error('institution not found');
  }

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    workshop.institutionID,
    MailTemplateType.MEETING_INFOS,
  )(InstitutionMailTemplate.allFields);

  const mailVars = {
    [MailTemplateVars.TITLE]: user?.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.PRODUCT_NAME]: workshop.name,
    [MailTemplateVars.PRODUCT_CARD]: InstitutionMailTemplate.mapProductVar(workshop as Workshop),
    [MailTemplateVars.MEETING_LINK]: `<a href="${appointment.meetingLink?.link}">${appointment.meetingLink?.link}</a>`,
    [MailTemplateVars.EVALUATION_LINK]:
      workshop.settings.evaluationLink ?? workshop.settings.inheritedEvaluationLink?.[1] ?? '',
    [MailTemplateVars.EVALUATION_CODE]:
      workshop.settings.evaluationLinkCode ?? workshop.settings.inheritedEvaluationLinkCode?.[1] ?? '',
  };

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    // TODO: implement whatsapp
  } else if (notificationType === 'email') {
    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const mailContent = await mailTemplate.buildEmail(mailVars as any);
    if (!mailContent) throw new Error('mail template has no content!');
    await sendInstitutionEmail({
      institutionID: workshop.institutionID,
      email: user.email,
      subject,
      content,
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
